import { atom } from "jotai";

import type {
	ShipmentCarrier,
	ShipmentCustomer,
	ShipmentDetailsData,
} from "~/shared/schemas/shipment-management-schema.ts";

import type { StopSpecialInstructions } from "../utils/format-special-instructions.ts";

/**
 * True while the user is editing the shipment detail sections (special
 * instructions, freight details, reference details). Only one shipment page is
 * mounted at a time, so a single atom is sufficient. Any section on the page can
 * read this to swap its read-only display for form inputs without prop drilling.
 */
export const isEditingShipmentAtom = atom<boolean>(false);

/**
 * Editable working copy of the shipment details. Seeded (cloned from the loaded
 * shipment) when the user enters edit mode and cleared on cancel/unmount. Every
 * edit section reads and writes its own slice here, so all inputs are controlled
 * by a single source of truth that can be submitted on save.
 */
export const shipmentDraftAtom = atom<ShipmentDetailsData | null>(null);

/**
 * Editable working copy of the shipment's carrier (PRO number and agent PRO).
 * The carrier lives outside `shipmentDetails`, so it gets its own draft slice,
 * seeded/cleared alongside {@link shipmentDraftAtom}.
 */
export const shipmentCarrierDraftAtom = atom<ShipmentCarrier | null>(null);

/**
 * Editable working copy of the shipment's customer (customer selection and
 * billing customer). The customer lives outside `shipmentDetails`, so it gets
 * its own draft slice, seeded/cleared alongside {@link shipmentDraftAtom}.
 */
export const shipmentCustomerDraftAtom = atom<ShipmentCustomer | null>(null);

/**
 * Editable working copy of the shipment-level accessorial selections. These are
 * stored separately from shipmentDetails because they live on the root shipment
 * payload, not inside the shipmentDetails object.
 */
export const shipmentAccessorialsDraftAtom = atom<string[] | null>(null);

/**
 * Each stop's own address special instructions (unmerged). Used to rebuild the
 * merged Special Instructions box when either stop's address is replaced.
 */
export const stopSpecialInstructionsDraftAtom =
	atom<StopSpecialInstructions | null>(null);

/**
 * True once the user has attempted to save, which enables inline validation
 * messages on the edit form. Reset when entering or leaving edit mode so a fresh
 * edit session starts without stale validation errors.
 */
export const shipmentValidationRequestedAtom = atom<boolean>(false);
